import axios from 'axios';
import html2canvas from 'html2canvas';
import { useEffect, useState } from 'react';
import Barcode from 'react-barcode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Account = () => {
   const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
   };

   const [centers, setCenters] = useState([]);
   const [form, setForm] = useState({
      accountNumber: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dob: '',
      country: '',
      state: '',
      borough: '',
      zip: '',
      address: '',
      city: '',
      center_name: "",
      rc_id: "",
      paymentMethod: '',
      paymentFrequency: '',
      paymentContact: '',
      ach_account_name: '',
      ach_account_number: '',
      ach_routing_number: '',
      ach_account_type: '',
      stripe_account_id: '',
      password: '',
      confirmPassword: '',
   });

   const [alert, setAlert] = useState({ message: '', type: '' });
   const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });
   const [barcodes, setBarcodes] = useState([]);
   const [showModal, setShowModal] = useState(false);

   const [tenantServiceAreas, setTenantServiceAreas] = useState([]);
   const [availableStates, setAvailableStates] = useState([]);
   const [availableBoroughs, setAvailableBoroughs] = useState([]);
   const [availableZips, setAvailableZips] = useState([]);
   const [noServiceArea, setNoServiceArea] = useState(false);


   useEffect(() => {
      if (alert.message) {
         const timer = setTimeout(() => {
            setAlert({ message: "", type: "" });
         }, 2500);
         return () => clearTimeout(timer);
      }
   }, [alert]);

   // Fetch Centers for Dropdown
   const fetchCenters = async (tenant_id) => {
      try {
         const res = await axios.get(
            `/auth/redemption-centers/edit?tenant_id=${tenant_id}`
         );
         setCenters(res.data);
      } catch (err) {
         console.error("Failed to fetch centers:", err);
      }
   };

   const fetchServiceAreas = async (tenant_id) => {
      try {
         const res = await axios.get(
            `/auth/tenant/service-area/${tenant_id}`
         );

         if (!res.data || res.data.length === 0) {
            setNoServiceArea(true);
            setTenantServiceAreas([]);
            setAvailableStates([]);
            setAvailableBoroughs([]);
            setAvailableZips([]);
            return;
         }

         setNoServiceArea(false);
         setTenantServiceAreas(res.data);

         const states = [...new Set(res.data.map(a => a.state))];
         setAvailableStates(states);
      } catch (err) {
         console.error("Failed to fetch service areas", err);
         setNoServiceArea(true);
      }
   };

   useEffect(() => {
      fetchCenters();

      const token = sessionStorage.getItem('token');

      const fetchUserData = async () => {
         try {
            const response = await axios.get(
               '/auth/account',
               { headers: { Authorization: `Bearer ${token}` } }
            );

            const user = response.data.user;
            setForm(prev => ({
               ...prev,
               accountNumber: user.accountNumber || '',
               firstName: user.first_name || '',
               lastName: user.last_name || '',
               email: user.email || '',
               phone: user.phone || '',
               dob: user.dob ? formatDate(user.dob) : '',
               country: user.country || '',
               state: user.state || '',
               borough: user.borough || '',
               zip: user.zip || '',
               address: user.address || '',
               city: user.city || '',
               center_name: user.center_name || '',
               rc_id: user.rc_id || '',
               paymentMethod: user.payment_method || '',
               paymentFrequency: user.paymentFrequency || '',
               paymentContact: user.paymentContact || '',
               ach_account_name: user.ach_account_name || '',
               ach_account_number: user.ach_account_number || '',
               ach_routing_number: user.ach_routing_number || '',
               ach_account_type: user.ach_account_type || '',
               stripe_account_id: user.stripe_account_id || '',
            }));
            if (user.tenant_id) {
               fetchCenters(user.tenant_id);
               fetchServiceAreas(user.tenant_id);
            }
         } catch (err) {
            console.error('Failed to fetch user data:', err);
         }
      };
      fetchUserData();
   }, []);

   useEffect(() => {
      if (!tenantServiceAreas.length || !form.state) return;
      const boroughs = tenantServiceAreas
         .filter(a => a.state === form.state)
         .map(a => a.borough);

      setAvailableBoroughs([...new Set(boroughs)]);

      if (form.borough) {
         const zips = tenantServiceAreas
            .filter(
               a =>
                  a.state === form.state &&
                  a.borough === form.borough
            )
            .map(a => a.zipcode);

         setAvailableZips([...new Set(zips)]);
      }
   }, [tenantServiceAreas, form.state, form.borough]);


   const fetchUserBarcodes = async () => {
      try {
         const token = sessionStorage.getItem('token');
         const res = await axios.get('/auth/barcodes', {
            headers: { Authorization: `Bearer ${token}` }
         });
         setBarcodes(res.data.barcodes || []);
         setShowModal(true);
      } catch (err) {
         console.error('Failed to fetch barcodes:', err);
         toast.error('Failed to load barcodes');
      }
   };

   const handleDownload = async (barcodeValue, index) => {
      const element = document.getElementById(`barcode-${index}`);
      if (!element) return;

      try {
         const canvas = await html2canvas(element, { scale: 3, useCORS: true });
         const link = document.createElement('a');
         link.href = canvas.toDataURL('image/png');
         link.download = `${barcodeValue}.png`;
         link.click();
      } catch (err) {
         console.error('Download failed:', err);
         toast.error('Failed to download barcode');
      }
   };

   const handleChange = (e) => {
      const { name, value } = e.target;

      setForm(prev => {
         let updated = { ...prev, [name]: value };

         if (name === "state") {
            const boroughs = tenantServiceAreas
               .filter(a => a.state === value)
               .map(a => a.borough);

            setAvailableBoroughs([...new Set(boroughs)]);
            setAvailableZips([]);
            updated.borough = "";
            updated.zip = "";
         }

         if (name === "borough") {
            const zips = tenantServiceAreas
               .filter(a => a.state === form.state && a.borough === value)
               .map(a => a.zipcode);

            setAvailableZips([...new Set(zips)]);
            updated.zip = "";
         }

         return updated;
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      // if (!areaStatus.valid) {
      //    setAlert({ message: 'Please ensure your ZIP is within our service area.', type: 'error' });
      //    return;
      // }
      if (form.password !== form.confirmPassword) {
         toast.error('Passwords do not match');
         return;
      }

      const updatePayload = { ...form };
      if (!form.password) {
         delete updatePayload.password;
         delete updatePayload.confirmPassword;
      }

      try {
         const token = sessionStorage.getItem('token');
         await axios.put('/auth/account', updatePayload, {
            headers: { Authorization: `Bearer ${token}` },
         });
         toast.success('Account updated successfully!');
         setForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
      } catch (err) {
         console.error('Failed to update account:', err);
         toast.error('Failed to update account');
      }
   };

   return (
      <div>
         <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-2xl mx-auto my-8 p-6 sm:p-6 md:p-8 bg-white rounded-lg shadow-[0_0_10px_0_#b2b2b2]">
               <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-[#9cc026]">Welcome User</h1>
                  <h6>You can view and edit your account details here.</h6>
               </div>

               {/* View Barcode Button */}
               <div className="mb-6 text-right">
                  <button onClick={fetchUserBarcodes} className="px-6 py-2 text-sm bg-[#54BE73] text-white font-semibold rounded-3xl hover:bg-green-700 transition">
                     View Barcodes </button>
               </div>

               {/* barcode modal */}
               {showModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                     <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl w-full relative">
                        <button onClick={() => setShowModal(false)}
                           className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl"> &times;
                        </button>
                        <h2 className="text-lg font-semibold text-[#9cc026] mb-4">Your Barcodes</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-h-[400px] overflow-y-auto">
                           {barcodes.map((b, index) => {
                              const isUnused = b.status === "unused";
                              return (
                                 <div key={index}
                                    className={`p-4 border border-gray-300 rounded-md bg-white flex flex-col items-center justify-center transition ${isUnused ? "" : "opacity-50 blur-[1px] pointer-events-none"}`} >
                                    {/* Barcode */}
                                    <div id={`barcode-${index}`} className="mb-2">
                                       <Barcode value={b.barcode} width={2} height={100}
                                          fontSize={16} displayValue={true} />
                                    </div>

                                    {/* Status Badge */}
                                    <span
                                       className={`px-3 py-1 text-xs font-semibold rounded-full mb-2
                  ${b.status === "unused" ? "bg-green-100 text-green-700" : ""}
                  ${b.status === "used" ? "bg-blue-100 text-blue-700" : ""}
                  ${b.status === "expired" ? "bg-gray-200 text-gray-600" : ""}
                  ${b.status === "inactive" ? "bg-yellow-100 text-yellow-700" : ""}
                  ${b.status === "cancelled" ? "bg-red-100 text-red-700" : ""}
                `}
                                    >
                                       {b.status.toUpperCase()}
                                    </span>
                                    {/* Created date */}
                                    <p className="text-xs text-gray-500 text-center mb-2"> Created: {new Date(b.created_at).toLocaleDateString()}</p>
                                    {/* Download button only for UNUSED */}
                                    {isUnused && (
                                       <button onClick={() => handleDownload(b.barcode, index)} className="bg-[#9cc026] hover:bg-[#7ea01e] text-white px-4 py-2 rounded-md text-sm">
                                          Download
                                       </button>
                                    )}
                                 </div>
                              );
                           })}
                        </div>
                     </div>
                  </div>
               )}


               <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Customer / Tenant Info */}
                  <div>
                     <label className="block text-sm font-medium mb-1">
                        Customer Info <span className="text-red-500">*</span>
                     </label>

                     <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                        <input type="text" name="accountNumber" value={form.accountNumber}
                           readOnly placeholder="Customer ID"
                           className="w-full p-2.5 rounded-md bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed" />
                     </div>
                  </div>

                  {/* Full Name */}
                  <div>
                     <label className="block text-sm font-medium mb-1">Full Name <span className="text-red-500">*</span></label>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="firstName" value={form.firstName} onChange={handleChange} required
                           placeholder="First Name" className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                        <input type="text" name="lastName" value={form.lastName} onChange={handleChange} required
                           placeholder="Last Name" className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>
                  </div>

                  {/* Email and Phone */}
                  <div>
                     <label className="block text-sm font-medium mb-1">Contact Info <span className="text-red-500">*</span></label>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email"
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                        <input type="tel" name="phone" value={form.phone} onChange={handleChange} required
                           placeholder="Phone Number" className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">Date of Birth</label>
                        <input name="dob" value={form.dob} onChange={handleChange}
                           type="date" required
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Country</label>
                        <select name="country" value={form.country} onChange={handleChange} required
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm">
                           <option value="">Select Country</option>
                           <option value="Romania">Romania</option>
                           <option value="USA">USA</option>
                           <option value="India">India</option>
                        </select>
                     </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">Redemption Center</label>
                        <select name="center_name" value={form.center_name} onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required >
                           <option value="">Select Center</option>
                           {centers.map((center) => (
                              <option key={center.id} value={center.center_name}>
                                 {center.center_name}
                              </option>
                           ))}
                        </select>
                     </div>

                     <div>
                        <label className="block text-sm font-medium mb-1">RC ID</label>
                        <input type="text" name="rc_id" value={form.rc_id} readOnly className="w-full p-2.5 rounded-md bg-gray-100 border text-sm" />
                     </div>
                  </div>

                  {/* Address */}


                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">City</label>
                        <input type="text" name="city" value={form.city}
                           onChange={handleChange} placeholder="City" required
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Pickup Address</label>
                        <input type="text" name="address" value={form.address} required
                           onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>
                  </div>

                  {/* Country, State, ZIP */}
                  <div>
                     <label className="block text-sm font-medium mb-1">Location Details</label>

                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                           <label className="block text-sm font-medium mb-1">State</label>
                           <select
                              name="state"
                              value={form.state}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                           >

                              <option value="">Select State</option>
                              {availableStates.map(s => (
                                 <option key={s} value={s}>{s}</option>
                              ))}
                           </select>
                        </div>
                        <div>
                           <label className="block text-sm font-medium mb-1">Borough</label>
                           <select
                              name="borough"
                              value={form.borough}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                              disabled={!form.state}
                           >
                              <option value="">Select Borough</option>
                              {availableBoroughs.map(b => (
                                 <option key={b} value={b}>{b}</option>
                              ))}
                           </select>
                        </div>
                        <div>
                           <label className="block text-sm font-medium mb-1">Zip</label>
                           <select
                              name="zip"
                              value={form.zip}
                              onChange={handleChange}
                              className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                              required
                              disabled={!form.borough}
                           >
                              <option value="">Select ZIP</option>
                              {availableZips.map(z => (
                                 <option key={z} value={z}>{z}</option>
                              ))}
                           </select>
                        </div>
                     </div>
                  </div>
                  {noServiceArea && (
                     <div className="p-4 rounded-md bg-red-50 border border-red-300 text-red-700 text-sm font-medium">
                        🚫 This tenant has no active service areas. Please contact support or try again later.
                     </div>
                  )}


                  {/* Payment Method */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">How do you want to be paid?</label>
                        <select name="paymentMethod" value={form.paymentMethod} onChange={handleChange} required
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm">
                           <option value="">Select Payment Method</option>
                           <option value="Cash">Cash</option>
                           <option value="ACH">ACH "Electronic Payment"</option>
                           <option value="Ev-voucher">EV charging voucher"</option>
                           <option value="Fuel-Voucher">Fuel Voucher</option>
                           <option value="Stock-credit">Stock-credit</option>
                           <option value="PayPal">PayPal</option>
                           <option value="Check by Mail">Check by Mail</option>
                        </select>
                        <p className="text-sm text-gray-600 mt-1">
                           Service Fees: <strong>10%</strong> per payment <em>(PayPal / Stripe fee)</em>
                        </p>
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Payment Frequency</label>
                        <div className="flex items-center gap-6">
                           <label className="text-sm font-medium inline-flex items-center">
                              <input
                                 type="radio"
                                 name="paymentFrequency"
                                 value="Weekly"
                                 checked={form.paymentFrequency === 'Weekly'}
                                 onChange={handleChange}
                                 className="form-radio text-green-600"
                                 required
                              />
                              <span className="ml-2">Weekly</span>
                           </label>
                           <label className="text-sm font-medium inline-flex items-center">
                              <input
                                 type="radio"
                                 name="paymentFrequency"
                                 value="Daily"
                                 checked={form.paymentFrequency === 'Daily'}
                                 onChange={handleChange}
                                 className="form-radio text-green-600"
                              />
                              <span className="ml-2">Daily</span>
                           </label>
                        </div>
                     </div>
                  </div>

                  {/* Payment Email or Mobile */}
                  {form.paymentMethod === 'PayPal' && (
                     <div>
                        <label className="block text-sm font-medium mb-1">PayPal Email</label>
                        <input
                           type="email"
                           name="paymentContact"
                           value={form.paymentContact}
                           onChange={handleChange}
                           placeholder="PayPal Email"
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                           required
                        />
                     </div>
                  )}

                  {form.paymentMethod === 'ACH' && (
                     <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                           <div>
                              <label className="block text-sm font-medium mb-1">ACH Account Name</label>
                              <input
                                 type="text"
                                 name="ach_account_name"
                                 value={form.ach_account_name || ''}
                                 onChange={handleChange}
                                 className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                                 required
                              />
                           </div>
                           <div>
                              <label className="block text-sm font-medium mb-1">ACH Account Number</label>
                              <input type="text" name="ach_account_number" value={form.ach_account_number || ''} onChange={handleChange}
                                 className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                           </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                           <div>
                              <label className="block text-sm font-medium mb-1">ACH Routing Number</label>
                              <input type="text" name="ach_routing_number" value={form.ach_routing_number || ''} onChange={handleChange}
                                 className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                           </div>
                           <div>
                              <label className="block text-sm font-medium mb-1">ACH Account Type</label>
                              <select name="ach_account_type" value={form.ach_account_type || ''} required
                                 onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" >
                                 <option value="">Select Account Type</option>
                                 <option value="checking">Checking</option>
                                 <option value="savings">Savings</option>
                              </select>
                           </div>
                        </div>
                        <div>
                           <label className="block text-sm font-medium mb-1">Stripe Account ID</label>
                           <input type="text" name="stripe_account_id" value={form.stripe_account_id || ''} required
                              onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                        </div>
                     </>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {/* Password Fields */}
                     <div>
                        <label className="block text-sm font-medium mb-1">Password</label>
                        <input type="password" name="password" value={form.password}
                           onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                        />
                     </div>

                     <div>
                        <label className="block text-sm font-medium mb-1">Confirm Password</label>
                        <input type="password" name="confirmPassword" value={form.confirmPassword}
                           onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                        <p className="text-sm text-gray-600 mt-1 italic">
                           Leave the password field blank unless changing the password.
                        </p>
                     </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-4">
                     <button
                        type="submit"
                        className="w-fit px-4 py-2.5 bg-[#54BE73] hover:bg-green-700 text-white text-sm font-semibold rounded-3xl transition"
                     >
                        SAVE MY CHANGES
                     </button>
                  </div>
               </form>
               {alert.message && (
                  <div className={`mt-6 p-4 text-center font-semibold text-white rounded-md ${alert.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                     }`}>
                     {alert.message}
                  </div>
               )}
            </div>
         </div>
         <ToastContainer position="top-right" autoClose={3000} />
      </div>
   );
};

export default Account;
